import {
  Alert,
  Button,
  canUseAvatarUrl,
  Page,
  Skeleton,
  useFlags,
  useSnackbarContext,
  VerticalBox,
} from "@inductiveautomation/ws-components";
import { compact } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import AccountAppBar from "../../components/AccountAppBar/AccountAppBar";

import { baseApi } from "../../contexts/endpoints/base_api";
import {
  useDeleteAccountMutation,
  useGetProfileQuery,
  usePostResetPasswordMutation,
  usePutAdditionalEmailsMutation,
  usePutResendAdditionalEmailsVerifyMutation,
} from "./api/profile.api";
import { AccountFlags } from "../../featureFlags.ts";
import { useCurrentUser } from "../../hooks/currentUser";
import { handleResponse } from "../../utilities/api_response";
import { dataURLtoBlob } from "../../utilities/data_url_to_blob";
import { getProfileStyles } from "./_profile.styles";
import ChangePasswordDialog from "./dialogs/ChangePassword";
import ChangePrimaryDialog from "./dialogs/ChangePrimary";
import { emptyProfileData } from "./Profile.data";
import { OtherEmailCards, PrimaryEmailCard } from "./Profile.EmailCards";
import ProfileForm from "./Profile.Form";
import {
  GetProfileResponse,
  ModalKind,
  Profile as ProfileType,
} from "./Profile.types";
import ProfileModal from "./ProfileModal";
import PublicProfile from "./sections/PublicProfile";

const initialsFromProfile = (profile: Partial<ProfileType>) => {
  return `${profile.firstName?.[0] ?? ""}${profile.lastName?.[0] ?? ""}`;
};

const Profile = () => {
  const { t } = useTranslation("translation", { keyPrefix: "profile" });
  const navigate = useNavigate();

  const { classes } = getProfileStyles();
  const [currentModal, setCurrentModal] = useState<ModalKind>("none");
  const { addSnackbar } = useSnackbarContext();

  const {
    data: preTypedData = emptyProfileData,
    error: queryError,
    isLoading: queryIsLoading,
    refetch: refetchGetProfilePage,
  } = useGetProfileQuery();
  const data = preTypedData as GetProfileResponse;
  const [changePassword] = usePostResetPasswordMutation();
  const [uploadAvatar] = baseApi.usePutAvatarImageMutation();
  const [removeAvatar] = baseApi.useDeleteAvatarImageMutation();

  const [deleteAccount] = useDeleteAccountMutation();
  const [updateAdditionalEmails] = usePutAdditionalEmailsMutation();
  const [resendAdditionalEmailVerification] =
    usePutResendAdditionalEmailsVerifyMutation();
  const userInfo = useCurrentUser();
  const userData =
    userInfo.user && !userInfo.isLoading
      ? userInfo.user
      : { id: "", claims: undefined };

  const { tsAccountPublicProfileFe, tsAccountAdditionalEmailsFe } =
    useFlags<AccountFlags>();

  const handleProfileModalOnSubmit = async (email: string) => {
    if (currentModal === "add-email") {
      const additionalEmails = [
        ...data.additionalEmails.map((e) => e.email),
        email,
      ];
      const response = await updateAdditionalEmails({
        additionalEmailsPayload: {
          additionalEmails: compact(additionalEmails),
        },
      });
      handleResponse(response, {
        onSuccess: () => {
          addSnackbar(t("additional_emails.add_success_message"), {
            buttonLeftOnClick: () => undefined,
          });

          // NOTE: (2024-12-09) Noah Settersten => Since the back-end is eventually consistent, the refetch for Profile
          // data may receive a response that doesn't yet include the new email. To fix this, we refetch the Profile
          // data after a delay to ensure we have the updated emails.
          // This will no longer be needed once server-sent events are used to update front-end data after mutations.
          setTimeout(() => void refetchGetProfilePage(), 2_000);
        },
        onError: () => {
          addSnackbar(t("additional_emails.error_message"), {
            type: "error",
          });
        },
      });
    } else if (currentModal === "delete-account") {
      const response = await deleteAccount();
      handleResponse(response, {
        onSuccess: () => void navigate("/confirm/delete-account"),
        onError: () =>
          addSnackbar(t("delete_account.error_message"), {
            type: "error",
          }),
        fallbackErrorMessage: t("delete_account.error_message"),
      });
    }

    setCurrentModal("none");
  };

  const onResendVerification = (email: string) => {
    resendAdditionalEmailVerification({ email })
      .then((response) => {
        handleResponse(response, {
          onSuccess: () => {
            addSnackbar(t("additional_emails.resend_success_message"));
          },
          onError: () => {
            addSnackbar(t("additional_emails.resend_error_message"), {
              type: "error",
            });
          },
        });
      })
      .catch(() => {
        addSnackbar(t("additional_emails.resend_error_message"), {
          type: "error",
        });
      });
  };

  return (
    <>
      <AccountAppBar
        title={[
          {
            label: "Profile",
            id: "account-app-bar-profile",
            onClick: () => undefined,
          },
        ]}
      />
      <Page
        itemBannerProps={{
          avatarProps: queryIsLoading
            ? "loading"
            : {
                // 3/26/2025 - TODO: This should be handled by the backend. Consult note on canUseAvatar
                // anything we create will come from AWS
                ...(canUseAvatarUrl(data.avatarUrl)
                  ? {
                      imageUrl: data.avatarUrl,
                      variant: "image",
                    }
                  : {
                      // TODO: fix up the discrepancy between the ProfileType between what the backend returns and the wrapper over yup
                      text:
                        data.firstName || data.lastName
                          ? initialsFromProfile(data)
                          : data.primaryEmailAddress[0],
                      textForAvatarColor: userData.id,
                      variant: "text",
                    }),
              },
          onAvatarUploaded: (dataUrl: string | undefined) => {
            if (!dataUrl) return;
            const imageBlob = dataURLtoBlob(dataUrl);
            const formData = new FormData();
            formData.append("file", imageBlob);

            uploadAvatar(formData)
              .then((response) => {
                handleResponse(response, {
                  onSuccess: () => {
                    addSnackbar(t("upload_avatar.success_message"));
                    // NOTE: (2025-02-25) Eric N -> Similar to Noah's comment above. Backend will not necessarily be ready. Server side event needed and this will be udpated at that point.
                    setTimeout(() => void refetchGetProfilePage(), 1_500);
                  },
                  onError: (message) => {
                    console.error(message);
                    addSnackbar(t("upload_avatar.error_message"), {
                      type: "error",
                    });
                  },
                  fallbackErrorMessage: t("upload_avatar.error_message"),
                });
              })
              .catch((error: unknown) => {
                console.error(error);
                addSnackbar(t("upload_avatar.error_message"), {
                  type: "error",
                });
              });
          },
          onAvatarRemoval: () => {
            removeAvatar()
              .then((response) => {
                handleResponse(response, {
                  onSuccess: () => {
                    addSnackbar(t("remove_avatar.success_message"));
                    // NOTE: (2025-02-25) Eric N -> Similar to Noah's comment above. Backend will not necessarily be ready. Server side event needed and this will be udpated at that point.
                    setTimeout(() => void refetchGetProfilePage(), 2_000);
                  },
                  onError: (message) => {
                    console.error(message);
                    addSnackbar(t("remove_avatar.error_message"), {
                      type: "error",
                    });
                  },
                  fallbackErrorMessage: t("remove_avatar.error_message"),
                });
              })
              .catch((error: unknown) => {
                console.error(error);
                addSnackbar(t("remove_avatar.error_message"), {
                  type: "error",
                });
              });
          },
          // 3/26/2025 - TODO: This should be handled by the backend. Consult note on canUseAvatar
          // anything we create will come from AWS
          ...(!queryIsLoading && canUseAvatarUrl(data.avatarUrl)
            ? {
                avatarUrl: data.avatarUrl,
              }
            : {}),
        }}
        sections={[
          {
            heading: t("heading"),
            headingLevel: "h1",
            description: t("heading_description"),
            children: (
              <ProfileForm
                defaultValues={{
                  ...data,
                  phoneNumber: {
                    countryCode:
                      data.phoneNumber?.country || data.country || "US",
                    number: data.phoneNumber?.number ?? "",
                  },
                }}
                loading={queryIsLoading || !!queryError}
              />
            ),
          },
          ...(tsAccountPublicProfileFe
            ? [
                {
                  heading: t("public_profile.heading"),
                  description: t("public_profile.description"),
                  children: queryIsLoading ? (
                    <Skeleton variant="data_card" count={1} />
                  ) : (
                    <PublicProfile publicProfile={data.publicProfile} />
                  ),
                },
              ]
            : []),
          {
            heading: t("primary_email.heading"),
            description: t("primary_email.description"),
            children: queryIsLoading ? (
              <Skeleton variant="data_card" count={1} />
            ) : (
              <PrimaryEmailCard
                email={data.primaryEmailAddress || ""}
                onChangePrimary={() => {
                  setCurrentModal("change-primary-email");
                }}
              />
            ),
          },
          ...(tsAccountAdditionalEmailsFe
            ? [
                {
                  heading: t("additional_emails.heading"),
                  description: t("additional_emails.description"),
                  children: queryIsLoading ? (
                    <Skeleton variant="data_card" count={3} gap="0.5rem" />
                  ) : (
                    <VerticalBox gap="1rem">
                      <OtherEmailCards
                        emails={data.additionalEmails}
                        onResendVerification={onResendVerification}
                        onRemove={(email) => {
                          const newAdditionalEmails =
                            data.additionalEmails.filter(
                              (e) => e.email !== email
                            );
                          updateAdditionalEmails({
                            additionalEmailsPayload: {
                              additionalEmails: compact(
                                newAdditionalEmails.map((e) => e.email)
                              ),
                            },
                          })
                            .then((response) => {
                              handleResponse(response, {
                                onSuccess: () => {
                                  addSnackbar(
                                    t(
                                      "additional_emails.remove_success_message"
                                    )
                                  );
                                },
                                onError: () => {
                                  addSnackbar(
                                    t("additional_emails.error_message"),
                                    {
                                      type: "error",
                                    }
                                  );
                                },
                              });
                            })
                            .catch(() => {
                              addSnackbar(
                                t("additional_emails.error_message"),
                                {
                                  type: "error",
                                }
                              );
                            });
                        }}
                      />

                      <Button
                        buttonVariant="text"
                        label={t("additional_emails.add_email_action.label")}
                        testId="profile-add-email-action"
                        className={classes.nonFullWidth}
                        onClick={() => setCurrentModal("add-email")}
                      />
                    </VerticalBox>
                  ),
                },
              ]
            : []),
          {
            heading: t("password.heading"),
            description: userData.claims?.isSso
              ? t("password.managed_account_description")
              : t("password.description"),
            children: userData.claims?.isSso ? (
              <Alert
                id="managed_account_alert"
                variant="info"
                title={t("password.managed_account_alert.title")}
                description={t("password.managed_account_alert.description")}
                closeButtonEventHandler={() => undefined}
              />
            ) : (
              <Button
                buttonVariant="primary"
                label={t("change_password_action.label")}
                className={classes.nonFullWidth}
                onClick={() => {
                  changePassword()
                    .then((response) => {
                      handleResponse(response, {
                        onSuccess: () => setCurrentModal("change-password"),
                        onError: (message) =>
                          addSnackbar(
                            message ??
                              t("change_password_action.error_message"),
                            {
                              id: "reset-password-failed",
                              type: "error",
                            }
                          ),
                        fallbackErrorMessage: t(
                          "change_password_action.error_message"
                        ),
                      });
                    })
                    .catch(() => {
                      addSnackbar(t("change_password_action.error_message"), {
                        id: "reset-password-failed",
                        type: "error",
                      });
                    });
                }}
              />
            ),
          },
          {
            heading: t("delete_account.heading"),
            description: t("delete_account.description"),
            children: (
              <Button
                buttonVariant="destructive"
                label={t("delete_account.action")}
                className={classes.nonFullWidth}
                onClick={() => setCurrentModal("delete-account")}
              />
            ),
          },
        ]}
      >
        <ProfileModal
          currentModal={currentModal}
          primaryEmail={data.primaryEmailAddress || ""}
          additionalEmails={data.additionalEmails}
          onClose={() => setCurrentModal("none")}
          onSubmit={(email) => void handleProfileModalOnSubmit(email)}
        />

        <ChangePrimaryDialog
          isOpen={currentModal === "change-primary-email"}
          onClose={() => setCurrentModal("none")}
        />
        <ChangePasswordDialog
          isOpen={currentModal === "change-password"}
          email={data.primaryEmailAddress || ""}
          onClose={() => setCurrentModal("none")}
        />
      </Page>
    </>
  );
};

export default Profile;
